/**
 * Amazon Chime telehealth via Medplum Bot (`chime-telehealth`).
 */

import type { LocationConfirmationInput } from '@unify/fhir-model/resources/client-location-confirmation';
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';

export const CHIME_TELEHEALTH_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'chime-telehealth',
} as const;

export type ChimeTelehealthAction =
  | 'provider-join'
  | 'client-wait'
  | 'confirm-location'
  | 'location-status'
  | 'admit'
  | 'remove-guest'
  | 'leave'
  | 'end'
  | 'recording-start'
  | 'recording-stop'
  | 'session-status'
  | 'check-transcript-status';

/** The bot never sends `MeetingArn` or `ExternalMeetingId`; the SDK needs only these fields. */
export type ChimeMeetingPayload = {
  MeetingId?: string;
  MediaRegion?: string;
  MediaPlacement?: Record<string, string>;
  MeetingFeatures?: Record<string, unknown>;
};

/** `ExternalUserId` is `<base id>#<display name>` (design Decision 39); parse it with `parseChimeExternalUserId`. */
export type ChimeAttendeePayload = {
  AttendeeId?: string;
  ExternalUserId?: string;
  JoinToken?: string;
};

/** provider-join: why the join that created the meeting did not start recording (returned once, never stored). */
export type RecordingNotStartedReason = 'consent-missing' | 'consent-unavailable' | 'start-failed';

export type ChimeJoinResult = {
  /** client-wait: `ended` when the host ended the session. */
  state: 'joined' | 'waiting' | 'admitted' | 'ended';
  /** client-wait while waiting: `start` is more than 15 minutes past and no provider has joined. */
  providerLate?: boolean;
  /**
   * The session is recording (the recorder in Chime's roster, or the capture still starting). Only the value to show
   * until media connects: in the call, the recorder's roster presence decides.
   */
  recordingActive?: boolean;
  /**
   * client-wait: the session is recording and the request did not carry
   * `recordingAcknowledged: true`, so no credentials were minted. Show the notice, then re-call.
   */
  needsAcknowledgement?: boolean;
  /**
   * provider-join: true only when THIS join started the capture; false when it joined a running capture or none
   * started. Absent (an older server) means ownership is unknown.
   */
  captureStartedByThisJoin?: boolean;
  /** provider-join, only when this join created the meeting and recording did not start. */
  recordingNotStarted?: RecordingNotStartedReason;
  meeting?: ChimeMeetingPayload;
  attendee?: ChimeAttendeePayload;
};

/**
 * What the client does with a `client-wait` response: show the recording notice (no credentials
 * until acknowledged), connect with the minted attendee, keep waiting, or nothing (already
 * connecting, or an admitted response without credentials). `ended`: the host ended the session.
 */
export function clientWaitStep(
  result: ChimeJoinResult,
  started: boolean,
): 'acknowledge' | 'connect' | 'waiting' | 'ended' | 'none' {
  if (result.state === 'ended') return 'ended';
  if (result.state === 'waiting') return 'waiting';
  if (result.state !== 'admitted' || started) return 'none';
  if (result.needsAcknowledgement) return 'acknowledge';
  return result.meeting && result.attendee ? 'connect' : 'none';
}

export type ChimeAdmitResult = { state: 'admitted'; patientRef?: string; guestId?: string };
export type ChimeLeaveResult = { left: true };
/** `ended: false`: an in-person `end` for a meeting that is no longer current (replaced elsewhere); nothing was ended. */
export type ChimeEndResult = { ended: boolean };

/**
 * `recording-start` / `recording-stop`, and the fields `session-status` adds. Capture starts at once; there is no
 * pending state. `recordingActive` is the server's view; in the call, the recorder's roster presence decides.
 */
export type ChimeRecordingResult = {
  recordingActive: boolean;
  /** `session-status`, Practitioner participants only, while not recording: `recording-start` would resume a capture that ran. */
  canResume?: boolean;
  /** `session-status`: a provider's attendee is in the meeting. */
  providerPresent?: boolean;
  /** `session-status`, Practitioner participants only: open waiting-room rows (`[]` when nobody waits). */
  waiting?: ChimeWaitingItem[];
};

/** One person in the waiting room, as `session-status` reports it to a Practitioner participant. */
export type ChimeWaitingItem = {
  /** What `admit` and `remove-guest` take: the guest handle, or `Patient/<id>` for a portal client. */
  id: string;
  name: string;
  kind: 'patient' | 'guest';
  /** A personal-link participant, or a guest marked as a client. */
  participantRef?: string;
  /** The provider admitted them; they get credentials on their next poll. */
  admitted: boolean;
};
export type ChimeSessionStatusResult = ChimeRecordingResult & { sessionStatus: string };

export type ChimeTelehealthResult =
  | ChimeJoinResult
  | ChimeAdmitResult
  | ChimeLeaveResult
  | ChimeEndResult
  | ChimeRecordingResult
  | ChimeSessionStatusResult;

export function getChimeTelehealthBotExecuteTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_CHIME_TELEHEALTH_BOT_ID?.trim();
  if (id) return id;
  const system =
    import.meta.env.VITE_MEDPLUM_CHIME_TELEHEALTH_BOT_IDENTIFIER_SYSTEM?.trim() ??
    CHIME_TELEHEALTH_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_CHIME_TELEHEALTH_BOT_IDENTIFIER_VALUE?.trim() ??
    CHIME_TELEHEALTH_BOT_IDENTIFIER.value;
  return { system, value };
}

export async function executeChimeTelehealth(
  medplum: MedplumClient,
  input: {
    action: ChimeTelehealthAction;
    appointmentId?: string;
    patientId?: string;
    guestId?: string;
    /** admit (guest only): the `Patient/<id>` participant the provider marks the guest as. */
    markAsClient?: string;
    attendeeId?: string;
    /** client-wait: the client saw the recording notice and chose to join. */
    recordingAcknowledged?: boolean;
    /** provider-join, end, session-status: the in-person recording page. */
    inPerson?: boolean;
    /** end with `inPerson`: the meeting this page recorded in, so a stale End is ignored. */
    expectedMeetingId?: string;
    /**
     * leave: the provider pressed Leave (not a page unload). Only this stops a running capture, when no other
     * provider remains. Never sent on unload: see `releaseChimeSessionOnUnload`.
     */
    final?: boolean;
  },
): Promise<ChimeTelehealthResult> {
  return medplum.executeBot(
    getChimeTelehealthBotExecuteTarget(),
    input,
    ContentType.JSON,
  ) as Promise<ChimeTelehealthResult>;
}

/**
 * Saves the signed-in Patient's own session location confirmation. The bot writes it: patients cannot update the
 * Appointment.
 */
export async function confirmClientSessionLocation(
  medplum: MedplumClient,
  appointmentId: string,
  location: LocationConfirmationInput,
): Promise<void> {
  await medplum.executeBot(
    getChimeTelehealthBotExecuteTarget(),
    { action: 'confirm-location', appointmentId, location },
    ContentType.JSON,
  );
}

/**
 * Whether the signed-in Patient already confirmed their session location. The bot reads it: the confirmation is
 * kept in the Appointment's private record, which patients cannot read.
 */
export async function fetchClientSessionLocationStatus(
  medplum: MedplumClient,
  appointmentId: string,
): Promise<boolean> {
  const result = (await medplum.executeBot(
    getChimeTelehealthBotExecuteTarget(),
    { action: 'location-status', appointmentId },
    ContentType.JSON,
  )) as { confirmed?: boolean };
  return result?.confirmed === true;
}

export function botExecuteUrl(medplum: MedplumClient, target: string | Identifier): string {
  const base = medplum.getBaseUrl().replace(/\/$/, '');
  return typeof target === 'string'
    ? `${base}/fhir/R4/Bot/${target}/$execute`
    : `${base}/fhir/R4/Bot/$execute?identifier=${encodeURIComponent(`${target.system}|${target.value}`)}`;
}

/**
 * The body of an unload release: `leave` (or `end`) for the caller's attendee, never `final`. A page unload may be a
 * refresh or a dropped provider about to rejoin, so recording keeps running (Decision 39); only the provider's
 * explicit Leave sends `final: true`.
 */
export function unloadReleaseBody(input: {
  appointmentId: string;
  attendeeId?: string;
  action?: 'leave' | 'end';
}): { action: 'leave' | 'end'; appointmentId: string; attendeeId?: string } {
  const { action = 'leave', appointmentId, attendeeId } = input;
  return { action, appointmentId, ...(attendeeId ? { attendeeId } : {}) };
}

/**
 * Best-effort `leave` (or `end`) while the page is unloading. `executeBot` uses a normal fetch, which the browser
 * cancels on teardown, leaving the attendee in the meeting and the caller in the waiting list. `keepalive` lets the
 * request outlive the document. Never `final` ({@link unloadReleaseBody}).
 */
export function releaseChimeSessionOnUnload(
  medplum: MedplumClient,
  input: { appointmentId: string; attendeeId?: string; action?: 'leave' | 'end' },
): void {
  const token = medplum.getAccessToken();
  if (!token) return;
  void fetch(botExecuteUrl(medplum, getChimeTelehealthBotExecuteTarget()), {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': ContentType.JSON,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(unloadReleaseBody(input)),
  }).catch(() => undefined);
}

/**
 * The explicit Leave's fallback when `leaveChimeSessionFinal` failed or timed out: the same `leave` with `final: true`
 * as a keepalive request, so the provider's choice to stop recording still reaches the bot as they navigate away.
 * Only the Leave button uses it; a page unload never sends `final` ({@link releaseChimeSessionOnUnload}).
 */
export function retryFinalLeave(medplum: MedplumClient, input: { appointmentId: string; attendeeId?: string }): void {
  const token = medplum.getAccessToken();
  if (!token) return;
  void fetch(botExecuteUrl(medplum, getChimeTelehealthBotExecuteTarget()), {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': ContentType.JSON,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...unloadReleaseBody(input), final: true }),
  }).catch(() => undefined);
}

/**
 * The provider's explicit Leave (design Decision 39): `leave` with `final: true`, which stops recording when no other
 * provider remains. Page unloads and dropped connections use {@link releaseChimeSessionOnUnload} instead.
 */
export async function leaveChimeSessionFinal(
  medplum: MedplumClient,
  input: { appointmentId: string; attendeeId?: string },
): Promise<ChimeLeaveResult> {
  return (await executeChimeTelehealth(medplum, {
    action: 'leave',
    appointmentId: input.appointmentId,
    ...(input.attendeeId ? { attendeeId: input.attendeeId } : {}),
    final: true,
  })) as ChimeLeaveResult;
}

/** Rejects with `message` when `promise` has not settled within `ms`. */
export function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
